/**
 * useResolvedDisplayValues
 *
 * Batch-resolves human-readable display strings for a set of URIs
 * (predicates and URI-valued objects) so the entity page can show
 * friendly text instead of raw prefixed URIs.
 *
 * Built around a small registry of "synthesis patterns" — each knows
 * which one-hop properties it needs and how to format a display
 * string from them. All patterns are resolved in a SINGLE SPARQL
 * query, scoped to the given URI set via VALUES, so adding more
 * patterns does not add extra round trips and stays cheap regardless
 * of how many node types are supported.
 *
 * Priority order (first match wins) is the order of PATTERNS below:
 *   1. rdfs:label / skos:prefLabel
 *   2. railml3:Designator (hasEntry [+ hasRegister])
 *   3. railml3:ElementState (hasValue)
 *   4. time:Instant (inXSDDate)
 *
 * A URI may legitimately have several values for the winning pattern
 * (typically an untagged label plus one in the active locale, or a
 * Designator with several entries). All of them are returned, sorted
 * so the rendered order does not depend on the order the endpoint
 * happens to return rows in. Ranking labels by language preference is
 * deliberately NOT done here — see the FILTER in the label pattern,
 * which already narrows candidates to untagged + active locale (or all
 * Norwegian variants when the locale is nb/no/nn).
 *
 * Passing an empty URI list suppresses the query entirely.
 * Results are cached per (language, URI set); switching the toggle on and off does not re-query.
 *
 * Degrades gracefully to an empty map if the user is not logged in
 * or the query fails — callers should always fall back to
 * shortenUri() when no resolved value is present.
 */

import { useEffect, useState } from 'react';
import sparqlService from '@services/sparql.service';
import type { SparqlBinding } from '@/types';

const RDFS_NS = 'http://www.w3.org/2000/01/rdf-schema#';
const SKOS_NS = 'http://www.w3.org/2004/02/skos/core#';
const RAILML3_NS = 'http://ontology.railml.org/railml3#';
const TIME_NS = 'http://www.w3.org/2006/time#';

/**
 * Display strings per URI. More than one entry means the source data
 * holds several equally valid values (e.g. one label per language);
 * the caller decides how to join them.
 */
export type ResolvedDisplayValues = Record<string, string[]>;

/**
 * A single "synthesis pattern": a named group of one-hop SPARQL
 * variables plus a formatter that turns bound values into display
 * text. Adding a new pattern is a single registry entry — it does
 * NOT add a new query round trip, only a few more OPTIONAL clauses
 * to the one batched query.
 */
interface SynthesisPattern {
    /** Pattern identifier, used for readability and debugging only. */
    name: 'label' | 'designator' | 'elementState' | 'timeInstant';
    /** Variable names (without leading `?`) this pattern binds. */
    vars: string[];
    /** SPARQL OPTIONAL block(s) fetching this pattern's properties for ?uri. */
    sparqlBlock: string;
    /** Build the display string from this pattern's bound values; null = no match. */
    format: (row: SparqlBinding) => string | null;
}

const PATTERNS: SynthesisPattern[] = [
    {
        name: 'label',
        vars: ['label'],
        sparqlBlock: `OPTIONAL {
    ?uri rdfs:label|skos:prefLabel ?label .
    {{langFilter}}
  }`,
        format: (row) => row.label?.value ?? null,
    },
    {
        name: 'designator',
        vars: ['designatorEntry', 'designatorRegister'],
        sparqlBlock: `OPTIONAL {
    ?uri railml3:hasEntry ?designatorEntry .
    OPTIONAL { ?uri railml3:hasRegister ?designatorRegister }
  }`,
        format: (row) => {
            const entry = row.designatorEntry?.value;
            if (!entry) return null;
            const register = row.designatorRegister?.value;
            return register ? `${entry} (${register})` : entry;
        },
    },
    {
        name: 'elementState',
        vars: ['elementStateValue'],
        sparqlBlock: `OPTIONAL { ?uri railml3:hasValue ?elementStateValue }`,
        format: (row) => row.elementStateValue?.value ?? null,
    },
    {
        name: 'timeInstant',
        vars: ['timeInstantDate'],
        sparqlBlock: `OPTIONAL { ?uri time:inXSDDate ?timeInstantDate }`,
        format: (row) => row.timeInstantDate?.value ?? null,
    },
];

const LANG_SUBTAG = /^[a-zA-Z]{2,3}$/;

function normalizeLang(preferredLang: string): string {
    const base = (preferredLang || '').split('-')[0];
    return LANG_SUBTAG.test(base) ? base.toLowerCase() : 'en';
}

// Norwegian data uses mixed @no/@nb/@nn tagging — match all three for any Norwegian locale.
const NORWEGIAN_LANGS = new Set(['nb', 'no', 'nn']);

function buildLangFilter(lang: string): string {
    const clauses = ['LANG(?label) = ""'];
    if (NORWEGIAN_LANGS.has(lang)) {
        clauses.push('LANGMATCHES(LANG(?label), "nb")');
        clauses.push('LANGMATCHES(LANG(?label), "no")');
        clauses.push('LANGMATCHES(LANG(?label), "nn")');
    } else {
        clauses.push(`LANGMATCHES(LANG(?label), "${lang}")`);
    }
    return `FILTER(${clauses.join(' || ')})`;
}

function buildQuery(uris: string[], lang: string): string {
    const values = uris.map((u) => `<${u}>`).join(' ');
    const allVars = PATTERNS.flatMap((p) => p.vars).map((v) => `?${v}`).join(' ');
    const langFilter = buildLangFilter(lang);
    const blocks = PATTERNS.map((p) => p.sparqlBlock.replace('{{langFilter}}', langFilter)).join('\n  ');

    return `
PREFIX rdfs: <${RDFS_NS}>
PREFIX skos: <${SKOS_NS}>
PREFIX railml3: <${RAILML3_NS}>
PREFIX time: <${TIME_NS}>
SELECT DISTINCT ?uri ${allVars} WHERE {
  VALUES ?uri { ${values} }
  ${blocks}
}
  `.trim();
}

/**
 * Fold result rows into display values.
 *
 * OPTIONAL blocks combine as a cross product, so one URI can span
 * several rows. For each URI the highest-priority pattern that matched
 * anywhere wins, and every distinct value that pattern produced is kept.
 */
function collectDisplayValues(bindings: SparqlBinding[]): ResolvedDisplayValues {
    const best = new Map<string, { rank: number; texts: string[] }>();

    for (const row of bindings) {
        const uri = row.uri?.value;
        if (!uri) continue;

        for (let rank = 0; rank < PATTERNS.length; rank++) {
            const text = PATTERNS[rank].format(row);
            if (!text) continue;

            const current = best.get(uri);
            if (!current || rank < current.rank) {
                best.set(uri, { rank, texts: [text] });
            } else if (rank === current.rank && !current.texts.includes(text)) {
                current.texts.push(text);
            }
            break; // first matching pattern for this row
        }
    }

    const values: ResolvedDisplayValues = {};
    for (const [uri, { texts }] of best) {
        values[uri] = [...texts].sort((a, b) => a.localeCompare(b));
    }
    return values;
}

// Results are cached per (language, URI set). Failed queries are not cached so they retry.
const resolutionCache = new Map<string, ResolvedDisplayValues>();
const CACHE_LIMIT = 50;

function writeCache(cacheKey: string, resolved: ResolvedDisplayValues): void {
    if (resolutionCache.size >= CACHE_LIMIT) {
        const oldest = resolutionCache.keys().next();
        if (!oldest.done) resolutionCache.delete(oldest.value);
    }
    resolutionCache.set(cacheKey, resolved);
}

export function useResolvedDisplayValues(
    uris: string[],
    preferredLang = 'en'
): ResolvedDisplayValues {
    const [values, setValues] = useState<ResolvedDisplayValues>({});
    const key = Array.from(new Set(uris)).filter(Boolean).sort().join('|');
    const lang = normalizeLang(preferredLang);
    const cacheKey = `${lang}|${key}`;

    useEffect(() => {
        // No URIs means the caller does not want resolution right now. Keep
        // whatever was resolved last instead of clearing: it is keyed by URI
        // and only read when the caller asks for friendly names, so a stale
        // map is never displayed, and this avoids a re-render on every toggle.
        if (!key) return;

        const cached = resolutionCache.get(cacheKey);
        if (cached) {
            setValues(cached);
            return;
        }

        let cancelled = false;

        sparqlService
            .query(buildQuery(key.split('|'), lang))
            .then((bindings) => {
                const resolved = collectDisplayValues(bindings);
                writeCache(cacheKey, resolved);
                if (!cancelled) setValues(resolved);
            })
            .catch(() => {
                if (!cancelled) setValues({});
            });

        return () => {
            cancelled = true;
        };
    }, [cacheKey, key, lang]);

    return values;
}